import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import BackLink from '../components/BackLink';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import LogoSpinner from '../components/ui/LogoSpinner';
import EmptyState from '../components/ui/EmptyState';
import Panel from '../components/ui/Panel';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import PageLayout from '../components/ui/PageLayout';
import { IconButton, buttonClass } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import { Field } from '../components/ui/Field';
import Select from '../components/ui/Select';
import SearchField from '../components/ui/SearchField';
import SegTrack from '../components/ui/SegTrack';
import { RadioGroup, RadioRow } from '../components/ui/Radio';
import { IconPencil } from '../components/ui/icons';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';
import { useExiting } from '../components/ui/motion';
dayjs.extend(relativeTime);

const MondayInbox = () => {
  const api = useApi();
  const toast = useToast();
  const history = useHistory();
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [accepting, setAccepting] = useState(null);
  const [linkSelections, setLinkSelections] = useState({});
  const [mode, setMode] = useState({}); // { [importId]: 'create' | 'link' }
  const [eventSearch, setEventSearch] = useState({});
  const [selectedEvent, setSelectedEvent] = useState({});
  const [nameOverrides, setNameOverrides] = useState({});
  const [editingName, setEditingName] = useState(null);

  const fetchImports = useCallback(async () => {
    try {
      const data = await api.get('/monday-inbox?status=pending');
      setImports(data || []);
    } catch {
      toast.error('Failed to load Monday imports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImports();
    api.get('/users').then(data => {
      setUsers((data || []).filter(u => u.can_producer));
    }).catch(() => {});
    api.get('/events').then(data => setEvents(data || [])).catch(() => {});
  }, []);

  const handleAccept = async (imp) => {
    const userSelection = imp.linked_user?.id || linkSelections[imp.id];
    if (imp.monday_person_id && !imp.linked_user && !linkSelections[imp.id]) {
      toast.error('Please assign a Firefleet user to the Monday owner first');
      return;
    }

    setAccepting(imp.id);
    try {
      const itemData = typeof imp.item_data === 'string' ? JSON.parse(imp.item_data) : imp.item_data;
      const mondayDate = itemData['date_mkzj8fxe']?.value || null;

      const body = {
        name: nameOverrides[imp.id] || imp.item_name,
        event_date: mondayDate,
        created_by: userSelection,
      };

      // Only send link_user_id if this is a new linking (not already linked)
      if (!imp.linked_user && linkSelections[imp.id]) {
        body.link_user_id = linkSelections[imp.id];
      }

      const event = await api.post(`/monday-inbox/${imp.id}/accept`, { body });
      toast.success(`Event "${imp.item_name}" created`);
      history.push(`/events/${event.id}`);
    } catch (err) {
      toast.error(err?.message || 'Failed to accept import');
    } finally {
      setAccepting(null);
    }
  };

  const handleLinkToEvent = async (imp) => {
    const eventId = selectedEvent[imp.id];
    if (!eventId) {
      toast.error('Please select an event to link to');
      return;
    }

    setAccepting(imp.id);
    try {
      await api.post(`/monday-inbox/${imp.id}/link`, { body: { event_id: eventId } });
      setImports(prev => prev.filter(i => i.id !== imp.id));
      toast.success(`Linked to existing event`);
    } catch (err) {
      toast.error(err?.message || 'Failed to link');
    } finally {
      setAccepting(null);
    }
  };

  const handleDismiss = async (imp) => {
    try {
      await api.post(`/monday-inbox/${imp.id}/dismiss`);
      setImports(prev => prev.filter(i => i.id !== imp.id));
      toast.success('Import dismissed');
    } catch (err) {
      toast.error(err?.message || 'Failed to dismiss');
    }
  };

  // Find events that might match an import (by date proximity, then name search)
  const getSuggestedEvents = (imp) => {
    const search = (eventSearch[imp.id] || '').toLowerCase();
    const itemData = typeof imp.item_data === 'string' ? JSON.parse(imp.item_data) : imp.item_data;
    const mondayDate = itemData['date_mkzj8fxe']?.value || null;

    const unlinked = events.filter(e => !e.monday_item_id);

    // If user is searching, filter by search term
    if (search) {
      return unlinked
        .filter(e => e.name.toLowerCase().includes(search) || dayjs(e.event_date).format('MMM D YYYY').toLowerCase().includes(search))
        .slice(0, 8);
    }

    // Auto-suggest: events within 7 days of the Monday show date, sorted by closest
    if (mondayDate) {
      const target = dayjs(mondayDate);
      return unlinked
        .map(e => ({ ...e, daysDiff: Math.abs(dayjs(e.event_date).diff(target, 'day')) }))
        .filter(e => e.daysDiff <= 7)
        .sort((a, b) => a.daysDiff - b.daysDiff)
        .slice(0, 8);
    }

    return unlinked.slice(0, 8);
  };

  // A handled import leaves in place, then the rest close the gap.
  const shownImports = useExiting(imports, (imp) => String(imp.id));

  if (loading) return <LogoSpinner label="Loading the inbox" />;

  return (
    <PageLayout width="narrow" kind="workspace" className="pt-4 space-y-5">
      <div>
        <BackLink to="/events" label="Events" className="mb-2" />
        <h1 className="text-2xl font-bold text-primary">Monday Inbox</h1>
      </div>

      {shownImports.length === 0 ? (
        <EmptyState>No pending imports from Monday</EmptyState>
      ) : (
        <div className="space-y-3">
          {shownImports.map(({ item: imp, leaving }) => {
            const itemData = typeof imp.item_data === 'string' ? JSON.parse(imp.item_data) : imp.item_data;
            const mondayDate = itemData['date_mkzj8fxe']?.value || null;
            const isExpanded = expandedId === imp.id;
            const isAccepting = accepting === imp.id;
            const currentMode = mode[imp.id] || 'create';
            const suggested = isExpanded ? getSuggestedEvents(imp) : [];

            return (
              <Reveal key={imp.id} show={!leaving}>
              <Panel
                label={imp.item_name}
                padding="none"
                className="overflow-hidden"
              >
                {/* Summary row */}
                <div
                  className="px-5 py-4 flex items-center gap-4 cursor-pointer hover:bg-gray-700/30 transition-colors duration-150"
                  onClick={() => setExpandedId(isExpanded ? null : imp.id)}
                >
                  <div className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-on-surface font-medium truncate">{imp.item_name}</div>
                    <div className="text-on-surface-variant text-xs mt-0.5">
                      {mondayDate ? dayjs(mondayDate).format('MMM D, YYYY') : 'No date'}
                      {imp.monday_person_name && (
                        <span className="ml-2 text-on-surface-disabled">
                          Owner: {imp.monday_person_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-on-surface-variant text-xs">{dayjs(imp.created_at).fromNow()}</span>
                  <svg
                    className={`w-4 h-4 text-on-surface-variant transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>

                {/* Expanded detail */}
                <Reveal show={isExpanded}>
                  <div className="px-5 pb-4 border-t border-gray-700/50 pt-4 space-y-4">
                    {/* Monday data preview */}
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                      <div>
                        <span className="text-on-surface-variant">Name</span>
                        {editingName === imp.id ? (
                          <Field
                            autoFocus
                            size="sm"
                            aria-label="Event name"
                            value={nameOverrides[imp.id] ?? imp.item_name}
                            onChange={e => setNameOverrides(prev => ({ ...prev, [imp.id]: e.target.value }))}
                            onBlur={() => setEditingName(null)}
                            onKeyDown={e => { if (e.key === 'Enter') setEditingName(null); }}
                          />
                        ) : (
                          <div className="text-on-surface flex items-center gap-1.5 group">
                            <span className={nameOverrides[imp.id] ? 'text-primary' : ''}>{nameOverrides[imp.id] || imp.item_name}</span>
                            <Tooltip label="Edit name"><IconButton label="Edit name" size="sm" onClick={e => { e.stopPropagation(); setEditingName(imp.id); }}>
                              <IconPencil />
                            </IconButton></Tooltip>
                          </div>
                        )}
                      </div>
                      <div>
                        <span className="text-on-surface-variant">Show Date</span>
                        <div className="text-on-surface">{mondayDate ? dayjs(mondayDate).format('MMM D, YYYY') : '--'}</div>
                      </div>
                      <div>
                        <span className="text-on-surface-variant">Owner</span>
                        <div className="text-on-surface">{imp.monday_person_name || '--'}</div>
                      </div>
                      <div>
                        <span className="text-on-surface-variant">Drones</span>
                        <div className="text-on-surface">{itemData['numeric_mkzmge46']?.text || '--'}</div>
                      </div>
                      <div className="col-span-2">
                        <span className="text-on-surface-variant">Location</span>
                        <div className="text-on-surface">{itemData['location_mkzj2jsm']?.text || '--'}</div>
                      </div>
                      {(itemData['_contacts'] || []).length > 0 && (
                        <div className="col-span-2">
                          <span className="text-on-surface-variant">Contacts</span>
                          <div className="text-on-surface space-y-0.5">
                            {itemData['_contacts'].map((c, i) => (
                              <div key={i} className="text-xs">
                                {c.name}{c.email && <span className="text-on-surface-variant ml-2">{c.email}</span>}{c.phone && <span className="text-on-surface-variant ml-2">{c.phone}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div>
                        <span className="text-on-surface-variant">Deal Value</span>
                        <div className="text-on-surface">{itemData['deal_value']?.text ? `$${Number(itemData['deal_value'].text).toLocaleString()}` : '--'}</div>
                      </div>
                      <div>
                        <span className="text-on-surface-variant">Stage</span>
                        <div className="text-on-surface">{itemData['deal_stage']?.text || '--'}</div>
                      </div>
                    </div>

                    {/* Mode toggle */}
                    <SegTrack
                      label="Import action"
                      size="sm"
                      value={currentMode}
                      onChange={key => setMode(prev => ({ ...prev, [imp.id]: key }))}
                      options={[
                        { key: 'create', label: 'Create New Event' },
                        { key: 'link', label: 'Link to Existing Event' },
                      ]}
                    />

                    <FadeIn key={currentMode} className="space-y-4">
                    {currentMode === 'create' ? (
                      <>
                        {/* Person linking — required */}
                        {imp.monday_person_id && (
                          <div className="bg-gray-900/60 rounded-md p-3">
                            {imp.linked_user ? (
                              <>
                                <div className="text-xs text-on-surface-variant mb-1">Monday owner "{imp.monday_person_name}" linked to:</div>
                                <div className="text-sm text-green-400 font-medium">
                                  {imp.linked_user.firstname} {imp.linked_user.lastname}
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="text-xs text-orange-400 mb-2 font-medium">
                                  Assign "{imp.monday_person_name}" to a Firefleet producer:
                                </div>
                                <Select
                                  label="Firefleet producer"
                                  placeholder="-- Select producer --"
                                  value={linkSelections[imp.id] ? String(linkSelections[imp.id]) : null}
                                  onChange={key => setLinkSelections(prev => ({ ...prev, [imp.id]: key }))}
                                  options={users.map(u => ({ key: String(u.id), label: `${u.firstname} ${u.lastname}` }))}
                                />
                                <p className="text-[10px] text-on-surface-variant mt-1">
                                  This links the Monday person ID permanently — future imports will auto-assign.
                                </p>
                              </>
                            )}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2 pt-1">
                          <button
                            disabled={isAccepting || (imp.monday_person_id && !imp.linked_user && !linkSelections[imp.id])}
                            onClick={() => handleAccept(imp)}
                            className={buttonClass('filled', 'md')}
                          >
                            {isAccepting ? 'Creating...' : 'Accept & Create Event'}
                          </button>
                          <button
                            onClick={() => handleDismiss(imp)}
                            className={buttonClass('quiet', 'md')}
                          >
                            Dismiss
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Link to existing event */}
                        <div className="bg-gray-900/60 rounded-md p-3 space-y-3">
                          <div className="text-xs text-on-surface-variant font-medium">Search for an existing Firefleet event to link:</div>
                          <SearchField
                            label="Search events to link"
                            value={eventSearch[imp.id] || ''}
                            onChange={next => setEventSearch(prev => ({ ...prev, [imp.id]: next }))}
                            placeholder="Search events..."
                          />
                          {suggested.length > 0 ? (
                            <RadioGroup
                              label="Event to link"
                              hideLabel
                              name={`link-event-${imp.id}`}
                              value={selectedEvent[imp.id] != null ? String(selectedEvent[imp.id]) : null}
                              onChange={key => setSelectedEvent(prev => ({ ...prev, [imp.id]: suggested.find(e => String(e.id) === key)?.id ?? key }))}
                              className="max-h-48 overflow-y-auto p-1"
                            >
                              {suggested.map(evt => (
                                <RadioRow key={evt.id} value={String(evt.id)}>
                                  <div className="text-sm text-on-surface truncate">{evt.name}</div>
                                  <div className="text-xs text-on-surface-variant">
                                    {dayjs(evt.event_date).format('MMM D, YYYY')}
                                    <span className="ml-2 capitalize">{evt.status}</span>
                                  </div>
                                </RadioRow>
                              ))}
                            </RadioGroup>
                          ) : (
                            <p className="text-xs text-on-surface-variant">
                              {eventSearch[imp.id] ? 'No matching events found' : 'Showing events with similar names'}
                            </p>
                          )}
                        </div>

                        <div className="flex gap-2 pt-1">
                          <button
                            disabled={isAccepting || !selectedEvent[imp.id]}
                            onClick={() => handleLinkToEvent(imp)}
                            className={buttonClass('filled', 'md')}
                          >
                            {isAccepting ? 'Linking...' : 'Link to Selected Event'}
                          </button>
                          <button
                            onClick={() => handleDismiss(imp)}
                            className={buttonClass('quiet', 'md')}
                          >
                            Dismiss
                          </button>
                        </div>
                      </>
                    )}
                    </FadeIn>
                  </div>
                </Reveal>
              </Panel>
              </Reveal>
            );
          })}
        </div>
      )}
    </PageLayout>
  );
};

export default MondayInbox;
